import { CheckboxTickIcon, CircleCloseIcon } from 'assets/icons';
import { CustomButton, CustomCheckbox, CustomModal, SearchBar } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { ButtonType } from 'components/custom-button/CustomButton';
import { selectUserDetails } from 'containers/app/appSlice';
import { JobsCleverTapEvents } from 'containers/jobs/events';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { useAppSelector } from 'hooks';
import React, { FC, useEffect, useState } from 'react';
import { VoidFn } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { getInitials } from 'utils/commonUtils';

interface AssignableMembersModalProps {
  className?: string;
  list: AssignableMember[];
  onClose: VoidFn;
  selectedMembers: AssignableMember[];
  onSave: (memberArray) => void;
  isLoading?: boolean;
  lockedMemberId?: string | null;
  onMemberSelectionChange?: (member: AssignableMember, isSelected: boolean) => void;
}

const AssignableMembersModal: FC<AssignableMembersModalProps> = ({
  list,
  onClose,
  selectedMembers = [],
  onSave,
  isLoading = false,
  lockedMemberId = null,
  onMemberSelectionChange
}) => {
  const [searchText, setSearchText] = useState('');
  const [membersList, setMembersList] = useState([]);
  const [selectedMembersList, setSelectedMembersList] = useState(selectedMembers);

  const currentUser = useAppSelector(selectUserDetails);

  useEffect(() => {
    setSelectedMembersList(selectedMembers);
  }, [selectedMembers]);

  useEffect(() => {
    logAnalyticEvent(JobsCleverTapEvents.webAddTeamMemberPageView);
  }, []);

  useEffect(() => {
    if (list.length > 0) {
      const newList = list.map(member => {
        const isMemberSelected = selectedMembers.some(
          selectedMember => selectedMember.merchantId === member.merchantId
        );
        return { ...member, isSelected: isMemberSelected };
      });
      newList.sort((member1, member2) => {
        if (member1.isSelected === member2.isSelected) {
          return 0;
        } else if (member1.isSelected) {
          return -1;
        }
        return 1;
      });
      setMembersList(newList);
    }
  }, [list, selectedMembers]);

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const onSearchClear = () => {
    setSearchText('');
  };

  const handleMemberSelection = member => {
    // Don't allow deselecting locked member
    if (member.isSelected && member.merchantId === lockedMemberId) {
      return;
    }

    const wasSelected = member.isSelected;

    if (wasSelected) {
      const updatedList = selectedMembersList.filter(selectedMember => selectedMember.merchantId !== member.merchantId);
      setSelectedMembersList(updatedList);
      const selectedMemberIndex = membersList.findIndex(listMember => listMember.merchantId === member.merchantId);
      const updatedMembersList = [...membersList];
      updatedMembersList[selectedMemberIndex].isSelected = false;
      setMembersList(updatedMembersList);
    } else {
      setSelectedMembersList([...selectedMembersList, member]);
      const selectedMemberIndex = membersList.findIndex(listMember => listMember.merchantId === member.merchantId);
      const updatedMembersList = [...membersList];
      updatedMembersList[selectedMemberIndex].isSelected = true;
      setMembersList(updatedMembersList);
    }

    // Trigger callback if provided
    if (onMemberSelectionChange) {
      onMemberSelectionChange(member, !wasSelected);
    }
  };

  const handleSave = () => {
    onSave(selectedMembersList);
  };

  const getMembers = () => {
    let members = membersList;
    if (searchText) {
      members = membersList.filter(member => {
        const hasName = member.name.toLowerCase().includes(searchText.toLowerCase());
        const hasEmail = member.email.toLowerCase().includes(searchText.toLowerCase());
        const hasPhone = member.phone.toLowerCase().includes(searchText.toLowerCase());

        return hasName || hasEmail || hasPhone;
      });
    }
    return members;
  };

  const renderMembersList = () => {
    const members = getMembers();
    if (members.length === 0) {
      return <div className="m-auto text-sbase font-semibold text-primaryText">No results found!</div>;
    }
    return members.map(member => {
      const isLocked = member.merchantId === lockedMemberId;
      return (
        <div
          key={member.merchantId}
          className={`flex cursor-pointer items-center gap-5 
          border-b border-borderGray py-3 last:border-b-0 hover:bg-highlightGray
          ${isLocked ? 'cursor-not-allowed opacity-70' : ''}`}
          onClick={e => {
            e.stopPropagation();
            if (!isLocked || !member.isSelected) {
              handleMemberSelection(member);
            }
          }}>
          <CustomCheckbox
            isChecked={member.isSelected}
            onClick={() => {
              if (!isLocked || !member.isSelected) {
                handleMemberSelection(member);
              }
            }}
            id={member.name}
            disabled={isLocked && member.isSelected}>
            {member.isSelected && <CheckboxTickIcon />}
          </CustomCheckbox>
          <CustomAvatar src={member.profilePictureUrl}>{getInitials(member.name || member.phone)}</CustomAvatar>
          <div className="max-w-[98%] break-words text-primaryText">
            {member.name}
            {member.merchantId === currentUser?.id && <span> {'(you)'}</span>}
          </div>
          <div className="text-accentText">{member.phone}</div>
        </div>
      );
    });
  };

  const selectedCount = selectedMembersList.length || 0;
  const totalMembersToShowCount = getMembers().length || 0;

  return (
    <CustomModal open={true} width="w-full md:w-[611px]" height="h-full md:max-h-[80vh]" enableOutsideClick={false}>
      <div className="relative flex h-full max-h-[80vh] flex-col">
        <div className="flex items-center justify-between px-10 pt-6 text-17px font-bold text-heading">
          Active members {`(${list.length})`}
          <CircleCloseIcon onClick={onClose} className="absolute top-3 right-3 cursor-pointer" />
        </div>
        <div className="px-10 py-4">
          <SearchBar
            showSearchIcon={true}
            placeholder="Search members"
            handleChange={handleSearch}
            value={searchText}
            handleClear={onSearchClear}
            id="assignable-members-search-bar"
            autoFocus={false}
          />
        </div>
        {totalMembersToShowCount > 0 && selectedCount > 0 && (
          <div className="px-10 pb-4 text-sbase text-accentText">
            {selectedCount} member{selectedCount === 1 ? '' : 's'} selected
          </div>
        )}
        <div className="customNormalScroll grow overflow-auto px-10">{renderMembersList()}</div>

        <div
          className="mt-auto flex items-center justify-end justify-items-end gap-4 
          border-t border-borderGray px-10 py-4">
          <CustomButton
            type={ButtonType.SECONDARY}
            className="h-10 w-[100px]"
            id="assign-member-modal-cancel"
            onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            type={ButtonType.PRIMARY}
            className="h-10 w-[100px]"
            id="assign-member-modal-save"
            onClick={handleSave}
            isLoading={isLoading}>
            Save
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AssignableMembersModal;
